import type { BuildingType, Cell, FieldId, MonsterType } from "../domain/types.js";
import type { SkillId } from "../domain/skills.js";

export interface CampaignMapConfig {
  rows: number;
  cols: number;
  fieldIds: readonly FieldId[];
  startingFieldId: FieldId;
  finalFieldId: FieldId;
  authoredFields: CampaignAuthoredFieldSource;
  fieldIdToCoords(fieldId: FieldId): Cell | null;
  isFieldPlayable(fieldId: FieldId): boolean;
  getAdjacentFieldIds(fieldId: FieldId): FieldId[];
}

export interface CampaignAuthoredField {
  fieldId: FieldId;
  fileName: string;
  yamlText: string;
}

export interface CampaignAuthoredFieldSource {
  get(fieldId: FieldId): CampaignAuthoredField | null;
}

export interface CampaignStoryPanel {
  imageSrc: string;
  textKey: string;
}

export interface CampaignStoryConfig {
  initialBeatId?: string;
  fieldWinBeats: Partial<Record<string, string>>;
  beats: Record<string, CampaignStoryPanel[]>;
}

export interface CampaignMapVisualConfig {
  backgroundSrc: string;
  decorations: readonly {
    imageSrc: string;
    x: number;
    y: number;
    scale: number;
  }[];
}

export interface CampaignBuildingVisualConfig {
  spriteSrc: string;
  battle?: {
    scaleX?: number;
    scaleY?: number;
    anchorY?: number;
    haloColor?: string;
    haloScale?: number;
    shadowAlpha?: number;
  };
}

export interface CampaignMonsterVisualConfig {
  sheetSrc: string;
  sheetFrameSize: number;
  sheetFrames: number;
  fallbackSrc: string;
  tint?: string;
  scale: number;
}

export interface CampaignPathEndpointVisualConfig {
  startStripSrc: string;
  startFallbackSrc: string;
  endSrc: string;
  battle?: {
    start?: CampaignEndpointBattleVisualConfig;
    end?: CampaignEndpointBattleVisualConfig;
  };
}

export interface CampaignEndpointBattleVisualConfig {
  scale?: number;
  glowScale?: number;
  glowColor?: string;
  ringColor?: string;
  particleColor?: string;
}

export type CampaignTerrainTileRole = "path" | "buildable" | "forest" | "rocks";

export type CampaignTerrainTileBases = Record<CampaignTerrainTileRole, string>;

export interface CampaignTerrainVisualConfig {
  tileBases: CampaignTerrainTileBases;
}

export interface CampaignStoryVisualConfig {
  imageAliases: Record<string, string>;
}

export interface CampaignFieldVisualOverride {
  map?: Partial<CampaignMapVisualConfig>;
  buildings?: Partial<Record<BuildingType, Partial<CampaignBuildingVisualConfig>>>;
  pathEndpoints?: Partial<CampaignPathEndpointVisualConfig>;
  monsters?: Partial<Record<MonsterType, Partial<CampaignMonsterVisualConfig>>>;
  terrain?: {
    tileBases?: Partial<CampaignTerrainVisualConfig["tileBases"]>;
  };
  story?: {
    imageAliases?: Record<string, string>;
  };
}

export interface CampaignVisualConfig {
  map: CampaignMapVisualConfig;
  buildings: Record<BuildingType, CampaignBuildingVisualConfig>;
  pathEndpoints: CampaignPathEndpointVisualConfig;
  monsters: Record<MonsterType, CampaignMonsterVisualConfig>;
  terrain: CampaignTerrainVisualConfig;
  story: CampaignStoryVisualConfig;
  fieldVisuals?: Partial<Record<FieldId, CampaignFieldVisualOverride>>;
}

export interface CampaignUiConfig {
  brandTitle: string;
  brandIcon: string;
  mageIcon: string;
  mageLabelKey: string;
  resourceLabelKey: string;
  waveTypeCodes: Record<MonsterType, string>;
  theme?: "classic" | "modern";
}

export interface CampaignAudioConfig {
  tracks: Record<"map" | "battle" | "win", string>;
}

export interface CampaignSkillConfig {
  hiddenSkillIds: readonly SkillId[];
  nameKeyPrefix: string;
  descriptionKeyPrefix: string;
}

export interface CampaignConfig {
  id: string;
  title: string;
  storageKey: string;
  tutorialStorageKey: string;
  map: CampaignMapConfig;
  story: CampaignStoryConfig;
  visual: CampaignVisualConfig;
  ui: CampaignUiConfig;
  audio: CampaignAudioConfig;
  skills: CampaignSkillConfig;
}

export function resolveCampaignVisualForField(
  visual: CampaignVisualConfig,
  fieldId: FieldId | null | undefined
): CampaignVisualConfig {
  const override = fieldId ? visual.fieldVisuals?.[fieldId] : undefined;
  if (!override) return visual;

  return {
    ...visual,
    map: {
      ...visual.map,
      ...override.map,
    },
    buildings: {
      ...visual.buildings,
      ...Object.fromEntries(
        Object.entries(override.buildings ?? {}).map(([type, building]) => [
          type,
          {
            ...visual.buildings[type as BuildingType],
            ...building,
          },
        ])
      ),
    },
    pathEndpoints: {
      ...visual.pathEndpoints,
      ...override.pathEndpoints,
    },
    monsters: {
      ...visual.monsters,
      ...Object.fromEntries(
        Object.entries(override.monsters ?? {}).map(([type, monster]) => [
          type,
          {
            ...visual.monsters[type as MonsterType],
            ...monster,
          },
        ])
      ),
    },
    terrain: {
      ...visual.terrain,
      ...override.terrain,
      tileBases: {
        ...visual.terrain.tileBases,
        ...override.terrain?.tileBases,
      },
    },
    story: {
      ...visual.story,
      ...override.story,
      imageAliases: {
        ...visual.story.imageAliases,
        ...override.story?.imageAliases,
      },
    },
  };
}
